import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from 'react-bootstrap';
import i18n from 'i18n';
import { RtbCampaignSetupFlowPageModel } from '../RtbCampaignSetupFlowPageModel';
import styles from './chooseProductsStep.module.scss';
import { ProductListDataContext } from 'containers/RetailMomo/Products/ProductListDataContext';
import { Product, ProductWithPagination, Retail } from 'core/product/Product';
import { MaxCVRHome } from 'containers/RetailMomo/Products/MaxCVR/ProductHome';
import _, { defaultTo, get, isNil, omitBy, uniq, xor } from 'lodash';
import { Modal } from 'components/Modal/Modal';
import { AdType } from 'core/rtbCampaign/RtbCampaign';
import { toast } from 'react-toastify';
import { SelectOptions } from 'components/commonType';
import { ProductFilter } from 'containers/RetailMomo/Products/ProductFilter';
import { useOrderStore } from 'store/orderStore';
import { DefaultRdpManager, RdpManager } from 'core/rdp/RdpManager';
import { useCoreContext } from 'contexts/coreContext';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import { LoadingIndicator } from 'components/LoadingIndicator';

export type BindProduct = {
  productId: string;
  retailId: string;
  name: string;
  category: string;
  imgLink: string;
};

export type ChooseProductsStepProps = {
  goNext: () => void;
  goLast: () => void;
  lockRemainSteps: () => void;
  flowModel: RtbCampaignSetupFlowPageModel;
  bindProducts?: Product[];
};

export const ChooseMaxCVRProductsStep: React.FunctionComponent<
  ChooseProductsStepProps
> = ({ goNext, goLast, lockRemainSteps, flowModel }) => {
  const adType = flowModel.state.campaign.basic.adType;
  const type = flowModel.type;
  const campaign = flowModel.state.campaign;
  const [autoProducts, setSelectedAutoProducts] = useState<Product[]>(
    defaultTo(flowModel.state.campaign.autoProducts, []),
  );
  const [products, setSelectedProducts] = useState<Product[]>(
    defaultTo(flowModel.state.campaign.products, []),
  );
  const productType = (pattern) => {
    if (pattern === 'AUTO') {
      return autoProducts;
    }
    return products;
  };
  const [productChangeAlert, setProductChangeAlert] = useState<boolean>(false);
  const hasSetupKeywordTA = useMemo(() => {
    const limitationModel = flowModel.limitationModel;
    const keywordTAError = limitationModel
      ? get(limitationModel.state.errors, 'include.searchKeywords')
      : undefined;
    const includeTA = get(flowModel.state.campaign.limitations, 'include', []);
    const searchKeywordsTA = includeTA.find(
      (ta) => ta.type === 'searchKeywords',
    );
    return (
      (!!searchKeywordsTA && searchKeywordsTA.value.length > 0) ||
      !!keywordTAError
    );
  }, [flowModel.limitationModel, flowModel.state.campaign]);

  const needUpdateProducts = useMemo(() => {
    const oriProducts = defaultTo(
      flowModel.state.campaign.basic.selectPattern === 'AUTO'
        ? flowModel.state.campaign.autoProducts
        : flowModel.state.campaign.products,
      [],
    );
    const oriProductIds = uniq(oriProducts.map((p) => p.productId));
    const selectedProductIds = uniq(
      productType(flowModel.state.campaign.basic.selectPattern).map(
        (p) => p.productId,
      ),
    );
    return xor(oriProductIds, selectedProductIds).length > 0;
  }, [
    flowModel.state.campaign.products,
    flowModel.state.campaign.autoProducts,
    products,
    autoProducts,
    flowModel.state.campaign.basic.selectPattern,
  ]);

  const needResetKeywords = useMemo(() => {
    return hasSetupKeywordTA && needUpdateProducts;
  }, [needUpdateProducts, hasSetupKeywordTA]);

  const { loading, callAPIs } = useCallAPI();
  const core = useCoreContext();
  const agencyId = _.get(core, 'accountManager.localeMeta.agencyId');
  const [filters, setFilters] = useState<ProductFilter>(
    _.defaultTo(
      {
        advertiser: useOrderStore.getState().order.advertiserId,
      },
      {},
    ),
  );
  const [retailerTypeOptions, setRetailerTypeOptions] = useState<
    (SelectOptions & { name: string })[]
  >([]);
  const [cache, setCache] = useState<{ [key: string]: any }>({});
  const { handleAutoSelectedProducts } = useContext(ProductListDataContext);
  const allProductList = useRef<Product[]>([]);
  const advertiser = filters.advertiser;
  const defaultRdpManager: RdpManager = new DefaultRdpManager();
  const { lockBrand } = useCampaignStore();
  const { changeSelectedProducts } = useProductStore();
  const fetchRetailerOptions = useCallback(
    async (advertiser: number) => {
      if (!agencyId) {
        return;
      }

      await callAPIs(
        [
          defaultRdpManager.getRetailsOfAdvertiser.bind(
            defaultRdpManager,
            agencyId,
            advertiser,
          ),
        ],
        (retailers: Retail[]) => {
          const newRetailerTypeOptions = [
            ...retailers.map((retailer) => ({
              label: retailer.name,
              value: retailer.id,
              name: retailer.name,
            })),
          ];
          setRetailerTypeOptions(newRetailerTypeOptions);
        },
      );
    },
    [agencyId, callAPIs],
  );

  const setInitRetailer = useCallback((initRetailer, cache) => {
    const cacheKey = `${initRetailer[0].name}_${initRetailer[0].value}`;
    if (cache[cacheKey]) {
      return;
    }
    setFilters((filter) => {
      const originRetailerId = _.get(filter, 'retailerId', '');
      const retailer = initRetailer.find(
        (retailer) => retailer.value.toString() === originRetailerId,
      );
      return {
        ...filter,
        retailerId: retailer
          ? originRetailerId
          : initRetailer[0].value.toString(),
      };
    });
    setCache({
      [cacheKey]: initRetailer,
    });
  }, []);

  const [brand, setBrand] = useState<string | null>(null);

  const defaultBrand = useMemo(() => {
    return brand;
  }, [brand]);

  const fetchProductsData = useCallback(
    async (page: number | undefined = 1) => {
      if (!_.isEmpty(filters) && !_.isNil(filters.advertiser)) {
        const retailId = _.get(filters, 'retailerId', '');
        // const queries = handleSearchQueries(filters, categories);
        if (_.isEmpty(retailId)) {
          return;
        }
        if (flowModel.type === 'edit') {
          if (
            (defaultBrand === flowModel.state.campaign.basic.webBrandNo &&
              lockBrand) ||
            (autoProducts.length > 0 &&
              !flowModel.state.campaign.basic.brandName)
          ) {
            console.warn('No need to fetch products');
            goNext();
            return;
          }
          if (flowModel.type === 'edit') {
            if (
              defaultBrand === flowModel.state.campaign.basic.webBrandNo ||
              (autoProducts.length > 0 &&
                !flowModel.state.campaign.basic.brandName)
            ) {
              console.error('No need to fetch products');
              goNext();
              return;
            }
          }
          const pageable = { page, sizePerPage: 25 };
          await callAPIs(
            [
              defaultRdpManager.getRetailProducts.bind(
                defaultRdpManager,
                retailId,
                pageable,
                {
                  isMaxCvrSmartKeyword: true,
                  brand: flowModel.state.campaign.basic.brandName
                    ? flowModel.state.campaign.basic.brandName
                    : null,
                },
              ),
            ],
            (productWithPagination: ProductWithPagination) => {
              allProductList.current = _.uniqBy(
                allProductList.current.concat(productWithPagination.products),
                'productId',
              );
              changeSelectedProducts(() => productWithPagination.products);
              handleAutoSelectedProducts(() => productWithPagination.products);
              flowModel.state.loading = false;
              goNext();
            },
          );
        }
      }
    },
    [
      autoProducts,
      flowModel.state.campaign.basic.selectPattern,
      filters,
      callAPIs,
    ],
  );

  const updateCampaignAndGoNext = useCallback(
    (resetBidPrice: boolean) => {
      let campaign = flowModel.state.campaign;
      if (resetBidPrice) {
        campaign = {
          ...campaign,
          basic: {
            ...campaign.basic,
            bidPrice: undefined,
          },
        };
      }
      const limitationModel = flowModel.limitationModel;
      const includeTA = campaign.limitations.include;
      limitationModel &&
        limitationModel.clearLimitation('include', 'searchKeywords', true);
      flowModel.setCampaign({
        ...campaign,
        products: products,
        autoProducts: autoProducts,
        limitations: omitBy(
          {
            ...campaign.limitations,
            include: includeTA
              ? includeTA.filter(
                  (limitation) => limitation.type !== 'searchKeywords',
                )
              : undefined,
          },
          isNil,
        ),
      });
      setProductChangeAlert(false);
      goNext();
    },
    [
      goNext,
      flowModel,
      products,
      autoProducts,
      flowModel.state.campaign.basic.selectPattern,
    ],
  );

  const onGoNext = useCallback(() => {
    if (productType(flowModel.state.campaign.basic.selectPattern).length > 25) {
      toast.warn(i18n.t<string>('productList.messages.upperLimit'));
      return;
    }
    if (needResetKeywords) {
      setProductChangeAlert(true);
      return;
    }
    if (needUpdateProducts) {
      const campaign = flowModel.state.campaign;
      flowModel.setCampaign({
        ...campaign,
        products: products,
        autoProducts: autoProducts,
      });
    }
    if (
      campaign.basic.selectPattern === 'AUTO' &&
      adType === AdType.MAX_CVR_KEYWORD
    ) {
      setProductChangeAlert(false);
      fetchProductsData();
    } else {
      goNext();
    }
  }, [
    needResetKeywords,
    flowModel,
    products,
    autoProducts,
    needUpdateProducts,
    goNext,
    fetchProductsData,
    setProductChangeAlert,
  ]);

  const closeGoNextAlert = useCallback(() => {
    setProductChangeAlert(false);
  }, []);

  const renderProductChangeAlert = () => {
    return (
      <Modal
        title={i18n.t<string>('common.warning')}
        primaryButton={{
          title: i18n.t<string>('common.labels.yes'),
          callback: () => updateCampaignAndGoNext(true),
        }}
        dismiss={closeGoNextAlert}
      >
        {i18n.t<string>('campaign.messages.resetBidPrice')}
      </Modal>
    );
  };
  const renderSelectProductComponent = () => {
    return (
      <ProductListDataContext.Provider
        value={{
          selectedProducts: products,
          autoSelectedProducts: autoProducts,
          smartCampaignConfig: flowModel.state.smartCampaignConfig,
          handleSelectedProducts: (
            fn: (products: Product[]) => Product[],
          ): void => {
            setSelectedProducts(fn(products));
          },
          handleAutoSelectedProducts: (
            fn: (autoProducts: Product[]) => Product[],
          ): void => {
            alert('handleAutoSelectedProducts');
            setSelectedAutoProducts(fn(autoProducts));
          },
          handleFilterChanged: () => {},
        }}
      >
        <MaxCVRHome
          title={i18n.t<string>('campaign.labels.productSetting')}
          type={type}
          campaign={campaign}
        />
      </ProductListDataContext.Provider>
    );
  };
  useEffect(() => {
    if (flowModel.state.campaign.basic.webBrandNo !== undefined) {
      setBrand(flowModel.state.campaign.basic.webBrandNo);
    }
  }, [flowModel.state.campaign.basic.webBrandNo]);

  useEffect(() => {
    lockRemainSteps();
  }, [lockRemainSteps]);

  useEffect(() => {
    if (!_.isNil(advertiser)) {
      fetchRetailerOptions(advertiser);
    }
  }, [advertiser, fetchRetailerOptions]);

  useEffect(() => {
    if (!_.isEmpty(retailerTypeOptions)) {
      setInitRetailer(retailerTypeOptions, cache);
    }
  }, [setInitRetailer, retailerTypeOptions, cache]);

  return (
    <div className={styles.chooseProductsStep} id={adType}>
      {loading && <LoadingIndicator />}
      <div className={styles.productList}>
        {productChangeAlert && <>{renderProductChangeAlert()}</>}
        <>{renderSelectProductComponent()}</>
      </div>
      <div className={styles.buttonArea}>
        <Button variant='primary' size='sm' onClick={onGoNext}>
          {i18n.t<string>('campaign.buttons.completeAndCheck')}
        </Button>
        {flowModel.type === 'create' && (
          <Button variant='secondary' size='sm' onClick={goLast}>
            {i18n.t<string>('campaign.buttons.back')}
          </Button>
        )}
      </div>
    </div>
  );
};
